import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, exhaustMap, firstValueFrom, from, map, of, race, takeUntil, timer } from 'rxjs';
import { WaltResponse } from '@weavix/models/src/walt/walt';
import { MapWalt } from 'weavix-shared/models/weavix-map.model';
import { NewRelicService } from 'weavix-shared/services/new-relic.service';
import { WaltService } from 'weavix-shared/services/walt.service';

/** Outcome of a single refresh HTTP call: a response, a per-attempt timeout, or a transport error. */
type RefreshAttemptResult =
    | { kind: 'response', walt: WaltResponse }
    | { kind: 'timeout' }
    | { kind: 'error', status?: number };

/**
 * Runs the walt sidebar's Refresh: retries, per-attempt timeouts and the failed-refresh state.
 * Provided by each sidebar so its state ends with that sidebar; call {@link init} before use.
 */
@Injectable()
export class WaltRefreshService {
    private static readonly MAX_REFRESH_ATTEMPTS = 3;
    private static readonly REFRESH_ATTEMPT_TIMEOUT_MS = 6000;

    private readonly waltService = inject(WaltService);
    private readonly destroyRef = inject(DestroyRef);

    private owner: unknown;
    private walt: Signal<MapWalt>;
    private currentRefreshSessionId = 0;
    private readonly refreshing = signal(false);
    /** Server-time baseline set when a refresh session fails; null while healthy or refreshing. */
    private readonly refreshFailedBaseline = signal<number | null>(null);
    private readonly refreshRequests$ = new Subject<void>();
    /** Emits on reset, so an in-flight refresh for the old walt stops blocking new ones. */
    private readonly reset$ = new Subject<void>();
    private readonly refreshedWaltSubject$ = new Subject<MapWalt>();

    /** True while a refresh session is running. */
    readonly isRefreshing = this.refreshing.asReadonly();
    /** True while the walt's last report is no newer than the failure baseline (server time only). */
    readonly refreshFailed = computed(() => {
        const baseline = this.refreshFailedBaseline();
        return baseline !== null && WaltService.getLastReportedTimestamp(this.walt()) <= baseline;
    });
    /** Emits the walt record from a completed refresh session, for the sidebar to pass to the shared store. */
    readonly refreshed$: Observable<MapWalt> = this.refreshedWaltSubject$.asObservable();
    private readonly refreshOrchestration = toSignal(
        this.refreshRequests$.pipe(
            exhaustMap(() => {
                const refreshSessionId = ++this.currentRefreshSessionId;
                const waltId = this.walt().id;

                this.refreshFailedBaseline.set(null);
                this.refreshing.set(true);

                return from(this.runRefreshWithRetry(waltId, refreshSessionId)).pipe(
                    map(() => null),
                    takeUntil(this.reset$),
                );
            }),
        ),
        { initialValue: null as null },
    );

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.currentRefreshSessionId += 1;
        });

        this.refreshOrchestration();
    }

    /** Binds the service to its sidebar: `owner` is the component each refresh request is tied to, `walt` the sidebar's walt input. */
    init(owner: unknown, walt: Signal<MapWalt>): void {
        this.owner = owner;
        this.walt = walt;
    }

    /** Starts a refresh of the current walt; ignored while one is already running, reported and skipped before {@link init}. */
    refresh(): void {
        if (!this.walt) {
            NewRelicService.sendError(new Error('walt refresh requested before the refresh service was initialized'));
            return;
        }
        this.refreshRequests$.next();
    }

    /** Drops any in-flight refresh and clears the spinner and failed state, for when the sidebar moves to another walt. */
    reset(): void {
        this.currentRefreshSessionId += 1;
        this.refreshFailedBaseline.set(null);
        this.refreshing.set(false);
        this.reset$.next();
    }

    /** True once a newer refresh session has started, or the sidebar has moved to another walt or closed. */
    private isSuperseded(refreshSessionId: number): boolean {
        return refreshSessionId !== this.currentRefreshSessionId;
    }

    /**
     * Runs a single refresh HTTP call, racing it against a per-attempt timeout so a stuck request
     * is abandoned rather than blocking the retry loop.
     */
    private runSingleRefreshAttempt(waltId: string): Promise<RefreshAttemptResult> {
        const response$ = from(this.waltService.refreshWalt(this.owner, waltId)).pipe(
            map((walt): RefreshAttemptResult => ({ kind: 'response', walt })),
            catchError((e: unknown) => of<RefreshAttemptResult>({
                kind: 'error',
                status: e instanceof HttpErrorResponse ? e.status : undefined,
            })),
        );
        const timeout$ = timer(WaltRefreshService.REFRESH_ATTEMPT_TIMEOUT_MS).pipe(
            map((): RefreshAttemptResult => ({ kind: 'timeout' })),
        );

        return firstValueFrom(race(response$, timeout$));
    }

    /**
     * Sends one New Relic error for a refresh that ends failed after a timeout, network error or 5xx;
     * 4xx errors and unanswered responses report nothing (the backend already records those).
     */
    private reportRefreshFailure(waltId: string, attempts: number, reason: RefreshAttemptResult): void {
        if (reason.kind === 'error' && reason.status !== undefined && reason.status >= 400 && reason.status < 500) return;
        const message = reason.kind === 'timeout'
            ? 'walt refresh timed out'
            : 'walt refresh failed with an HTTP error';
        const attributes: Record<string, string | number> = { waltId, attempts };
        if (reason.kind === 'error' && reason.status !== undefined) attributes.status = reason.status;

        NewRelicService.sendError(new Error(message), attributes);
    }

    /**
     * Retries the refresh up to {@link MAX_REFRESH_ATTEMPTS} times, abandoning any attempt that
     * exceeds {@link REFRESH_ATTEMPT_TIMEOUT_MS} and retrying attempts the walt did not answer.
     */
    private async runRefreshWithRetry(waltId: string, refreshSessionId: number): Promise<void> {
        let lastResponse: WaltResponse | undefined;
        let gotAnyResponse = false;
        let lastFailureReason: RefreshAttemptResult | undefined;
        let attemptsMade = 0;

        for (let attempt = 0; attempt < WaltRefreshService.MAX_REFRESH_ATTEMPTS; attempt++) {
            if (this.isSuperseded(refreshSessionId)) return;

            attemptsMade++;
            const result = await this.runSingleRefreshAttempt(waltId);

            if (this.isSuperseded(refreshSessionId)) return;

            if (result.kind === 'response') {
                lastResponse = result.walt;
                gotAnyResponse = true;

                if (!WaltService.hasRefreshFailureAfterLatestUpdate(result.walt)) {
                    this.refreshedWaltSubject$.next(result.walt as MapWalt);
                    this.refreshing.set(false);
                    return;
                }
                continue;
            }

            lastFailureReason = result;
            if (result.kind === 'error') break;
        }

        if (this.isSuperseded(refreshSessionId)) return;

        this.refreshing.set(false);
        this.refreshFailedBaseline.set(gotAnyResponse
            ? WaltService.getLastReportedTimestamp(lastResponse as WaltResponse)
            : WaltService.getLastReportedTimestamp(this.walt()));

        if (gotAnyResponse) this.refreshedWaltSubject$.next(lastResponse as MapWalt);
        if (lastFailureReason) this.reportRefreshFailure(waltId, attemptsMade, lastFailureReason);
    }
}
